async function loadPolyfills() {
  if (
    typeof window !== 'undefined' &&
    typeof window.IntersectionObserver === 'undefined'
  ) {
    await require('intersection-observer');
  }
}
loadPolyfills();

import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { createGlobalStyle } from 'styled-components';
import Layout from '../components/Homepage';

import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import '../styles/main.css';
import { FM_ACTIONS } from '../utils/UtilFunctions';
import { getCategoryName, includedRegexList } from '../utils/getCategory';
import { handleTrack } from '../utils/tracker';

// Extend window type for gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
  }
}

// Lazy load AmpHomePage since it's only used for AMP pages
const AmpHomePage = dynamic(() => import('../components/AmpHome'), {
  ssr: true,
});

// Web Vitals tracking function
function sendToAnalytics(metric: {
  name: string;
  value: number;
  delta: number;
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
}) {
  // Log to console for debugging
  console.log('📊 Web Vital:', {
    name: metric.name,
    value: Math.round(
      metric.name === 'CLS' ? metric.value * 1000 : metric.value
    ),
    rating: metric.rating,
    id: metric.id,
  });

  // Send to Google Analytics 4 if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value
      ),
      metric_value: metric.value,
      metric_delta: metric.delta,
      metric_rating: metric.rating,
      non_interaction: true,
    });
  }

  // Send to custom analytics endpoint if needed
  // Example: fetch('/api/analytics', { method: 'POST', body: JSON.stringify(metric) });
}

export const GlobalStyle = createGlobalStyle`
    // this is the shared style
    @font-face {
      font-family: "Gilroy";
      font-weight: normal;
      src: url('https://assets.clear.in/cleartax/images/1744895945_1606117730_gilroy_regular.woff2') format('woff2');
      font-display: swap;
    }
    
    @font-face {
      font-family: "Gilroy";
      font-weight: 500;
      src: url('https://assets.clear.in/cleartax/images/1744896293_1606117853_gilroy_medium.woff2') format('woff2');
      font-display: swap;
    }
    
    @font-face {
      font-family: "Gilroy";
      font-weight: 600;
      src: url('https://assets.clear.in/cleartax/images/1744896500_1606117950_gilroy_semibold.woff2') format('woff2');
      font-display: swap;
    }
    
    @font-face {
      font-family: "Gilroy";
      font-weight: 700;
      src: url('https://assets.clear.in/cleartax/images/1744896596_1606118037_gilroy_bold.woff2') format('woff2');
      font-display: swap;
    }
    
    @font-face {
      font-family: "Gilroy";
      font-weight: 800;
      src: url('https://assets.clear.in/cleartax/images/1744896619_1606118078_gilroy_extrabold.woff2') format('woff2');
      font-display: swap;
    }
    
    body {
      font-family: 'Gilroy', sans-serif;
    }
  // anything else you would like to include
`;

const MyApp = ({
  Component,
  pageProps,
}: AppProps<{ pageType: string }>): JSX.Element => {
  const router = useRouter();

  useEffect(() => {
    if (window === undefined) return;

    //call handleTrack only for pages with no categoryName or constant
    const pageCategoryName = getCategoryName({
      regexList: includedRegexList,
      pageType: pageProps?.pageType,
    });
    handleTrack({
      label: window.location.pathname,
      action: FM_ACTIONS.pageview,
      categoryName: pageCategoryName || '',
    });
  }, [pageProps]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const email = localStorage.getItem('userEmail');
      if (email && typeof window.smartech === 'function') {
        window.smartech('identify', email);
      }
    } catch (e) {
      // Silently fail in environments where localStorage is restricted (incognito/restrictive storage policies)
    }
  }, []);

  // Track Web Vitals including INP
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Track all Core Web Vitals
    onCLS(sendToAnalytics); // Cumulative Layout Shift
    onFCP(sendToAnalytics); // First Contentful Paint
    // onFID(sendToAnalytics); // First Input Delay (will be deprecated)
    onINP(sendToAnalytics); // Interaction to Next Paint (new!)
    onLCP(sendToAnalytics); // Largest Contentful Paint
    onTTFB(sendToAnalytics); // Time to First Byte

    console.log('🔍 Web Vitals tracking initialized');
  }, []);

  if (router.pathname.includes('/amp')) {
    return (
      <>
        <AmpHomePage>
          <Component {...pageProps} />
        </AmpHomePage>
      </>
    );
  } else {
    return (
      <>
        <GlobalStyle />
        <Layout>
          <Component {...pageProps} />
        </Layout>
      </>
    );
  }
};

export default MyApp;
